import React from 'react';
import { WorkflowState } from '../../../models/enums/WorkflowState';
import { PortOpeningRequestsDirection } from '../../../models/enums/POR/PortOpeningRequestsDirection';
import WorkflowStateBadge from '../../common/WorkflowStateBadge';
import './ProgressBarBig.css';
import PortOpeningRequestOnPrem from '../../../models/entities/PortOpeningRequestOnPrem';
import { OnPremPortOpeningRequestsTypes } from '../../../models/enums/POR/OnPremPortOpeningRequestsTypes';
import OnPremPORequestObject from '../../../models/entities/OnPremPORequestObject';

interface ProgressBarBigProps {
	workflowState: WorkflowState;
	por: PortOpeningRequestOnPrem | OnPremPORequestObject;
	type: string;
	scope: string;
}

const ProgressBarBig = ({ workflowState, por, type, scope }: ProgressBarBigProps) => {
	const hasProgressBar = (): boolean => {
		const noProgressArray = [WorkflowState.Cancelled, WorkflowState.Rejected];
		return !noProgressArray.some((nws) => nws.value === workflowState.value);
	};

	const workflowInstance = () =>
		por instanceof PortOpeningRequestOnPrem ? por.workflowInstance : por.onPremWorkflowInstance;

	const wasEscalatedToArchitect = (): boolean => {
		let reviewFound = false;

		for (let i = 0; i < workflowInstance().onPremWorkflowInstanceStates.length; i++) {
			const state = workflowInstance().onPremWorkflowInstanceStates[i];

			if (state.action === 'review') {
				reviewFound = true;
			} else if (reviewFound && state.onPremWorkflowState?.value === 416 && state.action === 'Escalate') {
				return true;
			}
		}

		return workflowInstance().onPremWorkflowInstanceStates.some((state) => {
			return state.onPremWorkflowState?.value === 416 && state.action === 'Escalate';
		});
	};

	const wasEscalatedToOTCRM = (): boolean => {
		let reviewFound = false;

		for (let i = 0; i < workflowInstance().onPremWorkflowInstanceStates.length; i++) {
			const state = workflowInstance().onPremWorkflowInstanceStates[i];

			if (state.action === 'review') {
				reviewFound = true;
			} else if (reviewFound && state.onPremWorkflowState?.value === 418 && state.action === 'Escalate') {
				return true;
			}
		}

		return workflowInstance().onPremWorkflowInstanceStates.some((state) => {
			return state.onPremWorkflowState?.value === 418 && state.action === 'Escalate';
		});
	};

	const wasEscalatedToCyberTeam = (): boolean => {
		return workflowInstance().onPremWorkflowInstanceStates.some((state) => {
			const conditionMet = state.onPremWorkflowState?.value === 412 && state.action === 'Escalate';
			return conditionMet;
		});
	};

	const wasEscalatedToProxyArchitect = (): boolean => {
		return workflowInstance().onPremWorkflowInstanceStates.some((state) => {
			const conditionMet = state.onPremWorkflowState?.value === 421 && state.action === 'Escalate';
			return conditionMet;
		});
	};

	
	const wasAutomaticApproval = (): boolean => {
		const resetStates = [
			WorkflowState.Draft.value,
			WorkflowState.Review.value,
		];

		const getStatesSinceLastReset = () => {
			const states = workflowInstance().onPremWorkflowInstanceStates;

			let lastResetIndex = -1;

			for (let i = states.length - 1; i >= 0; i--) {
				if (resetStates.includes(states[i].onPremWorkflowState?.value)) {
					lastResetIndex = i;
					break;
				}
			}
			return lastResetIndex !== -1 ? states.slice(lastResetIndex + 1) : states;
		};

		const states = getStatesSinceLastReset();

		return states.some((state) => {
			if (type === 'InternalCertConnection') {
				return (
					state.performedByRoles === 'App' &&
					state.onPremWorkflowState?.value === WorkflowState.PendingConnOwnerApproval.value
				);
			} else {
				return (
					state.performedByRoles === 'App' &&
					state.onPremWorkflowState?.value === WorkflowState.PendingAppEndorserApproval.value
				);
			}
		});
	};


	const activeCloudNetworkApproval = () => {
		if (wasAutomaticApproval()) return 'big-back-gray';
		switch (workflowState.value) {
			case WorkflowState.PendingSolutionArchitectureApproval.value:
			case WorkflowState.PendingConnOwnerApproval.value:
			case WorkflowState.PendingAppEndorserApproval.value:
			case WorkflowState.PendingForCyberApproval.value:
			case WorkflowState.PendingProxyArchitectApproval.value:
			case WorkflowState.PendingOTCRMApproval.value:
			case WorkflowState.PendingForDOAGApproval.value:
			case WorkflowState.AwaitingImplementation.value:
			case WorkflowState.Completed.value:
			case WorkflowState.Error.value:
			case WorkflowState.ImplementationFailed.value:
				return 'big-back-blue';
			default:
				return '';
		}
	};

	const activeSolutionArchitectureApproval = () => {
		switch (workflowState.value) {
			case WorkflowState.PendingForCyberApproval.value:
			case WorkflowState.PendingForDOAGApproval.value:
			case WorkflowState.PendingConnOwnerApproval.value:
			case WorkflowState.PendingProxyArchitectApproval.value:
			case WorkflowState.PendingAppEndorserApproval.value:
			case WorkflowState.AwaitingImplementation.value:
			case WorkflowState.Completed.value:
			case WorkflowState.Error.value:
			case WorkflowState.ImplementationFailed.value:
				return wasEscalatedToArchitect() ? 'big-back-blue' : 'big-back-gray';
			default:
				return '';
		}
	};

	const activeCyberApproval = () => {
		switch (workflowState.value) {
			case WorkflowState.PendingForDOAGApproval.value:
			case WorkflowState.PendingConnOwnerApproval.value:
			case WorkflowState.PendingAppEndorserApproval.value:
			case WorkflowState.AwaitingImplementation.value:
			case WorkflowState.Completed.value:
			case WorkflowState.Error.value:
			case WorkflowState.ImplementationFailed.value:
				return wasEscalatedToCyberTeam() ? 'big-back-blue' : 'big-back-gray';
			default:
				return '';
		}
	};

	const activeProxyArchitectApproval = () => {
		switch (workflowState.value) {
			case WorkflowState.PendingForDOAGApproval.value:
			case WorkflowState.PendingConnOwnerApproval.value:
			case WorkflowState.PendingAppEndorserApproval.value:
			case WorkflowState.AwaitingImplementation.value:
			case WorkflowState.Completed.value:
			case WorkflowState.Error.value:
			case WorkflowState.ImplementationFailed.value:
				return wasEscalatedToProxyArchitect() ? 'big-back-blue' : 'big-back-gray';
			default:
				return '';
		}
	};

	const activeOTCRMApproval = () => {
		if (type !== 'InternalCertConnection') return '';
		switch (workflowState.value) {
			case WorkflowState.PendingForDOAGApproval.value:
			case WorkflowState.PendingConnOwnerApproval.value:
			case WorkflowState.AwaitingImplementation.value:
			case WorkflowState.Completed.value:
			case WorkflowState.Error.value:
			case WorkflowState.ImplementationFailed.value:
				return wasEscalatedToOTCRM() ? 'big-back-blue' : 'big-back-gray';
			default:
				return '';
		}
	};

	const activeAppOwnerApproval = () => {
		switch (workflowState.value) {
			case WorkflowState.PendingForDOAGApproval.value:
			case WorkflowState.AwaitingImplementation.value:
			case WorkflowState.Completed.value:
			case WorkflowState.Error.value:
			case WorkflowState.ImplementationFailed.value:
				return 'big-back-blue';
			default:
				return '';
		}
	};

	const activeDOAGApproval = () => {
		switch (workflowState.value) {
			case WorkflowState.AwaitingImplementation.value:
			case WorkflowState.Completed.value:
			case WorkflowState.Error.value:
			case WorkflowState.ImplementationFailed.value:
				return 'big-back-blue';
			default:
				return '';
		}
	};

	const requiresDOAGApproval = () => {
		let oldRequest = !!por.approvingUser && !!por.appEndorser
		return oldRequest && por.type.name === OnPremPortOpeningRequestsTypes.InternetDMZ.name;
	};

	const isInternalCertConnection = () => por.type.name === OnPremPortOpeningRequestsTypes.InternalCertConnection.name;

	const bigLeftClass = (index: number): string => {
		const totalSteps = isInternalCertConnection() ? 5 : requiresDOAGApproval() ? 5 : 4;

		const position = Math.floor(
			(index * 100) / (totalSteps - 1)
		);

		return `big-left-${position}`;
	};

	return (
		<>
			<div className="big-progress-bar__title">
				<span className="em-c-field__label em-c-field__label--margin">Status</span>
				<WorkflowStateBadge workflowState={workflowState} />
			</div>
			{hasProgressBar() && (
				<div className="big-progress-bar">
					<div className="big-progress-bar__line" />
					<div className={`big-progress-bar__circle-with-title ${bigLeftClass(0)}`}>
						<div className={`big-progress-bar__circle ${activeCloudNetworkApproval()}`}>✓</div>
						<div className="big-progress-bar__circle--title">Network Policy Approval</div>
					</div>
					<div className={`big-progress-bar__circle-with-title ${bigLeftClass(1)}`}>
						<div className={`big-progress-bar__circle ${activeSolutionArchitectureApproval()}`}>✓</div>
						<div className="big-progress-bar__circle--title">Solution Architect Approval</div>
					</div>
					{scope !== PortOpeningRequestsDirection.iBossProxyToInternet.name ? (
						<>
							<div className={`big-progress-bar__circle-with-title ${bigLeftClass(2)}`}>
								<div className={`big-progress-bar__circle ${activeCyberApproval()}`}>✓</div>
								<div className="big-progress-bar__circle--title">Cyber Approval</div>
							</div>
						</>
					) : (
						<>
							<div className={`big-progress-bar__circle-with-title ${bigLeftClass(2)}`}>
								<div className={`big-progress-bar__circle ${activeProxyArchitectApproval()}`}>✓</div>
								<div className="big-progress-bar__circle--title">Proxy Architect Approval</div>
							</div>
						</>
					)}
					{type === OnPremPortOpeningRequestsTypes.InternalCertConnection.name && (
						<div className={`big-progress-bar__circle-with-title ${bigLeftClass(3)}`}>
							<div className={`big-progress-bar__circle ${activeOTCRMApproval()}`}>✓</div>
							<div className="big-progress-bar__circle--title">OTCRM Approval</div>
						</div>
					)}
					{type === OnPremPortOpeningRequestsTypes.InternetDMZ.name && (
						<div className={`big-progress-bar__circle-with-title ${bigLeftClass(3)}`}>
							<div className={`big-progress-bar__circle ${activeAppOwnerApproval()}`}>✓</div>
							<div className="big-progress-bar__circle--title">Application Owner Approval</div>
						</div>
					)}

					{requiresDOAGApproval() &&
						<div className={`big-progress-bar__circle-with-title ${bigLeftClass(4)}`}>
							<div className={`big-progress-bar__circle ${activeDOAGApproval()}`}>✓</div>
							<div className="big-progress-bar__circle--title">DOAG Approval</div>
						</div>
					}

					{type === OnPremPortOpeningRequestsTypes.InternalCertConnection.name && (
						<div className={`big-progress-bar__circle-with-title ${bigLeftClass(4)}`}>
							<div className={`big-progress-bar__circle ${activeDOAGApproval()}`}>✓</div>
							<div className="big-progress-bar__circle--title">Network Connection Owner Approval</div>
						</div>
					)}
				</div>
			)}
		</>
	);
};

export default ProgressBarBig;
